/* Your account — STORY-010. Export and erasure, which EPIC-001 calls a launch
 * blocker rather than a backlog item.
 *
 * ── why it is not inside Settings ────────────────────────────────────────────
 * Settings is the workspace: its Members, its currencies, its name. This is the
 * User, and a User outlives any workspace — the same account holds every
 * Membership. The API draws the same line, where account routes never set
 * tenant context. The list of workspaces below is the proof it belongs outside:
 * this screen knows about all of them, and nothing inside a workspace may.
 *
 * ── the whole design is that closing is not one confirmation ─────────────────
 * Almost all of STORY-010 is invisible — the erasure, the grace period, the
 * audit entry that survives the deletion it records. What IS a screen is that a
 * User who is the last Owner of a workspace has a decision to make about that
 * workspace, one per workspace, and until every one is answered there is
 * nothing to confirm. A single red button is how an Organisation gets silently
 * orphaned.
 *
 * ── not a modal ─────────────────────────────────────────────────────────────
 * The prototype found the decision structure does not fit a dialog: one of the
 * two answers is "I will hand it over first", which means leaving to promote
 * somebody. A dialog you cannot answer without leaving it is a trap in the bad
 * sense.
 */

import { formatDate } from '@eventuley/i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { ApiError, NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import {
  AuthScreen,
  Button,
  Callout,
  LanguagePicker,
  TextField,
} from '../../../shared/ui/index.js';
import { useCurrentUser } from '../../auth/index.js';
import { useAccount, useCancelClosure, useCloseAccount, useExportAccount } from '../api/index.js';

/** What each sole-owned workspace is to be done with. */
type Decision = 'close' | 'promote';

export function AccountScreen() {
  const t = useTranslate();
  const { formatLocale } = useI18n();
  const { data: user } = useCurrentUser();

  /* The workspace to return to. The first Membership is the one the chooser
   * would land on anyway; signed out or with none, no link is offered rather
   * than one that bounces to sign-in. */
  const backTo = user?.memberships[0]?.organisationId;
  const { data: account, error: accountError } = useAccount();
  const navigate = useNavigate();

  const exporting = useExportAccount();
  const closing = useCloseAccount();
  const cancelling = useCancelClosure();

  const [password, setPassword] = useState('');
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  /* The reader's own zone, not a venue's — this is a date about them. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const soleOwned = account?.soleOwned ?? [];

  /* Only `close` is an answer. `promote` is a statement of intent: the workspace
   * is still sole-owned until somebody else is an Owner, and when they are it
   * stops appearing here at all — the server derives that on every read. */
  const undecided = soleOwned.filter((row) => decisions[row.organisationId] !== 'close');
  const blocked = undecided.length > 0;

  /* An expired session is the likeliest failure on this screen, not the rarest:
   * it is reached from a menu, read slowly, and left open. Everywhere else in
   * the app that bounces to sign-in carrying where the reader was, and this
   * dead-ended on "something went wrong" until review on 2026-08-21.
   *
   * Once, and in an effect: navigating during render is not allowed, and the
   * ref stops a second pass overwriting the destination with wherever the first
   * one went — the same shape as the shell's. */
  const unauthenticated = accountError instanceof ApiError && accountError.status === 401;
  const bounced = useRef(false);
  useEffect(() => {
    if (!unauthenticated || bounced.current) return;
    bounced.current = true;
    void navigate({ to: '/sign-in', search: { redirect: '/account' }, replace: true });
  }, [unauthenticated, navigate]);

  if (accountError) {
    return (
      <AuthScreen title={t('nav.account')} brand={t('app.name')}>
        <Callout tone="danger">
          {accountError instanceof NetworkError
            ? t('error.network')
            : unauthenticated
              ? t('common.loading')
              : t('error.unexpected')}
        </Callout>
      </AuthScreen>
    );
  }

  /* Closed, and still reversible. The way back is on the screen rather than in
   * an email nobody keeps — and signing in does the same thing, so this is a
   * convenience rather than the only route. */
  if (account?.closesAt) {
    return (
      <AuthScreen title={t('nav.account')} brand={t('app.name')} subtitle={user?.email ?? ''}>
        <Callout tone="warning">
          <strong>
            {t('account.closed.title', {
              when: formatDate(new Date(account.closesAt), { locale: formatLocale, timeZone }),
            })}
          </strong>
          <p>{t('account.closed.body')}</p>
          <div className="btn-row">
            <Button busy={cancelling.isPending} onClick={() => cancelling.mutate()}>
              {t('account.closed.undo')}
            </Button>
          </div>
        </Callout>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen
      title={t('nav.account')}
      brand={t('app.name')}
      subtitle={user?.email ?? ''}
      /* The frame offers the control below the card for screens with nowhere
         else to put it. This one has somewhere — the section below, with a
         heading and a sentence saying whose setting it is. Two pickers on one
         screen is two places to answer one question. */
      offersLanguage={false}
      /* An account belongs to no Organisation, so this screen sits outside the
         workspace shell and has no navigation at all. Correct, and it left the
         browser's back button as the only way out. */
      back={backTo === undefined ? undefined : <BackToWorkspace organisationId={backTo} />}
      wide
    >
      <section className="settings__section">
        <h2>{t('language.heading')}</h2>
        {/* On the ACCOUNT and not in a workspace's settings: a person in three
            workspaces has one language, and STORY-042 needs it reachable before
            any Organisation exists. */}
        <p className="field__hint">{t('language.hint')}</p>
        <LanguagePicker />
      </section>

      <section className="settings__section">
        <h2>{t('account.export.heading')}</h2>
        <p className="field__hint">{t('account.export.body')}</p>
        {/* What it does NOT contain, said before it is asked for rather than
            discovered afterwards. A workspace's guests and money are the
            customer's data — that request goes to them, not to us. */}
        <p className="field__hint">{t('account.export.excludes')}</p>

        <Button busy={exporting.isPending} onClick={() => exporting.mutate()} variant="secondary">
          {t('account.export.submit')}
        </Button>

        {exporting.error ? (
          <Callout tone="danger">
            {exporting.error instanceof NetworkError
              ? t('error.network')
              : exporting.error instanceof ApiError && exporting.error.code === 'rate_limited'
                ? t('account.export.rate_limited')
                : t('error.unexpected')}
          </Callout>
        ) : null}

        {exporting.isSuccess && exporting.data ? (
          <Callout tone="success">
            <strong>{t('account.export.ready')}</strong>
            {/* Rendered rather than offered as a download link: the viewer
                sandbox aside, a file on a URL is a second copy of somebody's
                personal data that nobody is managing. This one exists only in
                the page they asked for it on. */}
            <pre className="account__export">{JSON.stringify(exporting.data, null, 2)}</pre>
          </Callout>
        ) : null}
      </section>

      <section className="settings__section">
        <h2>{t('account.close.heading')}</h2>
        <p className="field__hint">{t('account.close.body')}</p>

        {/* One card per workspace that needs an answer, and none at all when
            none do — an account with nothing to decide should not be shown an
            empty section implying it has forgotten something. */}
        {soleOwned.map((workspace) => (
          <Callout key={workspace.organisationId} tone="warning">
            <strong>{t('account.close.sole_owner', { name: workspace.name })}</strong>
            <p>{t('account.close.sole_owner_body')}</p>

            {/* A radio group, not two buttons: these are two answers to one
                question, and the chosen one has to stay visible afterwards. */}
            <div
              role="radiogroup"
              aria-label={t('account.close.what_happens', { name: workspace.name })}
            >
              {(['close', 'promote'] as const).map((choice) => (
                <label key={choice} className="account__choice">
                  <input
                    type="radio"
                    name={`decision-${workspace.organisationId}`}
                    value={choice}
                    checked={decisions[workspace.organisationId] === choice}
                    onChange={() =>
                      setDecisions((previous) => ({
                        ...previous,
                        [workspace.organisationId]: choice,
                      }))
                    }
                  />
                  <span>
                    {choice === 'close'
                      ? t('account.close.choice_close', { name: workspace.name })
                      : t('account.close.choice_promote')}
                  </span>
                </label>
              ))}
            </div>

            {decisions[workspace.organisationId] === 'close' ? (
              /* The subscription line is a criterion rather than decoration: a
                 closed Organisation still billing a deleted account is the
                 failure the story names explicitly. */
              <p>{t('account.close.close_warning', { count: String(workspace.otherMembers) })}</p>
            ) : null}

            {decisions[workspace.organisationId] === 'promote' ? (
              <p>
                <Link
                  to="/orgs/$organisationId/settings"
                  params={{ organisationId: workspace.organisationId }}
                >
                  {t('account.close.open_members')}
                </Link>
              </p>
            ) : null}
          </Callout>
        ))}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            closing.mutate({
              password,
              closeOrganisationIds: soleOwned
                .filter((row) => decisions[row.organisationId] === 'close')
                .map((row) => row.organisationId),
            });
          }}
          noValidate
        >
          {/* Re-authentication. A hijacked session must not be able to destroy
              an account, and this field is what makes that true. */}
          <TextField
            label={t('account.close.password_label')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {closing.error ? (
            <Callout tone="danger">
              {closing.error instanceof NetworkError
                ? t('error.network')
                : closing.error instanceof ApiError && closing.error.code === 'unauthenticated'
                  ? t('account.close.wrong_password')
                  : closing.error instanceof ApiError && closing.error.code === 'conflict'
                    ? t('account.close.undecided')
                    : t('error.unexpected')}
            </Callout>
          ) : null}

          {/* Blocked, and it SAYS which answer is missing rather than greying a
              button and leaving the reader to work out why. */}
          <p className="field__hint">
            {blocked
              ? t('account.close.blocked', {
                  names: undecided.map((row) => row.name).join(', '),
                })
              : t('account.close.grace', { days: String(account?.graceDays ?? 0) })}
          </p>

          <Button type="submit" variant="danger" busy={closing.isPending} disabled={blocked}>
            {t('account.close.submit')}
          </Button>
        </form>
      </section>

      <p className="auth__alt">
        <Link to="/next">{t('nav.switch_workspace')}</Link>
      </p>
    </AuthScreen>
  );
}

/**
 * The way out of a screen that has no navigation.
 *
 * A link to a destination rather than `history.back()`: a history step lands
 * somewhere different depending on how the screen was reached, and "back" that
 * is sometimes the sign-in page is worse than no button.
 */
function BackToWorkspace({ organisationId }: { readonly organisationId: string }) {
  const t = useTranslate();
  return (
    <Link className="back" to="/orgs/$organisationId/calendar" params={{ organisationId }}>
      {t('plans.back_to_workspace')}
    </Link>
  );
}
